import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, ReplaySubject, Subscription } from 'rxjs';
import { PolicyModel, PolicyToUpdateModel } from 'src/app/models/api/governance/policy.model';
import { HttpErrorHandlerService } from 'src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service';
import { NotificationsService } from 'src/app/services/infra/notifications/notifications.service';
import { IntegrationModel } from 'src/app/models/api';
import { StatusCodeEnum } from 'src/app/models/api/integration.model';
import { InactiveIntegrationDialogComponent } from '../inactive-integration-dialog/inactive-integration-dialog.component';
import { PoliciesHttpService } from 'src/app/services/httpServices/governance/policiesHttpService/policies-http.service';
import { EffectivePolicySettingEnum } from 'src/app/services/infra/store/model/effectivePolicySetting.model';
import { AddMcpServerDialogComponent } from '../add-mcp-server-dialog/add-mcp-server-dialog.component';
import _ from 'lodash';

export interface McpServerModel {
  url: string;
}

export interface IMcpServerViewModel {
  url: string;
  clickable: boolean;
}

@Component({
  selector: 'app-policy-mcp-server-filtering',
  templateUrl: './policy-mcp-server-filtering.component.html',
  styleUrls: ['./policy-mcp-server-filtering.component.scss'],
  encapsulation: ViewEncapsulation.None,
  styles: [
    `
      :host ::ng-deep .p-datatable .p-datatable-thead > tr > th {
        position: -webkit-sticky;
        position: sticky;
        top: 0px;
      }

      @media screen and (max-width: 64em) {
        :host ::ng-deep .p-datatable .p-datatable-thead > tr > th {
          top: 0px;
        }
      }
    `,
  ],
})
export class PolicyMcpServerFilteringComponent implements OnInit, OnDestroy {
  @Input() public policyModel: PolicyModel;
  @Input() public integrationModel: IntegrationModel;
  @Input() public editable = true;
  @Output() public policyUpdated = new EventEmitter<PolicyModel>();
  @Output() public notifyChanges = new EventEmitter<boolean>();
  @Output() public goBack = new EventEmitter<void>();

  public isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public mcpServersCurrentPage$: BehaviorSubject<IMcpServerViewModel[]> = new BehaviorSubject<IMcpServerViewModel[]>(
    []
  );
  public resetSearchBoxBehavioralSubject$: ReplaySubject<string[] | undefined> = new ReplaySubject<
    string[] | undefined
  >();
  public changesWereDone = false;
  public enableRemoveMcpServer = true;
  public mcpServersUrls: string[] = [];
  public tableNotFoundText = 'No MCP servers found';
  public tableNotFoundIcon = 'assets/images/common/no-results-found.svg';
  public EffectivePolicySettingEnum = EffectivePolicySettingEnum;

  private _mcpServersViewModel: IMcpServerViewModel[] = [];
  private _mcpServersSearchedViewModel: IMcpServerViewModel[] = [];
  private _policyConfigUpdateSubscription$: Subscription;
  private _policyEntitySubscription$: Subscription;

  constructor(
    private readonly _matDialog: MatDialog,
    private readonly _policiesHttpService: PoliciesHttpService,
    private readonly _httpErrorHandlerService: HttpErrorHandlerService,
    private readonly _notificationService: NotificationsService
  ) {}

  ngOnInit(): void {
    this._mcpServersViewModel =
      this.policyModel.config.mcpServers.map((mcpServer) => this._transformToMcpServerViewModel(mcpServer)) || [];
    this.mcpServersCurrentPage$.next(this._mcpServersViewModel);
    this._resetSearchBox();
  }

  ngOnDestroy(): void {
    this._policyConfigUpdateSubscription$?.unsubscribe();
    this._policyEntitySubscription$?.unsubscribe();
  }

  public mcpServerSearched(searchTerm: string[]): void {
    this._mcpServersSearchedViewModel = this._mcpServersViewModel
      .map((mcpServer) => this._transformToMcpServerViewModel(mcpServer))
      .filter((mcpServer) => searchTerm.includes(mcpServer.url));
    this.mcpServersCurrentPage$.next(this._mcpServersSearchedViewModel);
  }

  public addMcpServerClicked(): void {
    if (this.integrationModel.connectionStatus.code === StatusCodeEnum.off) {
      this._matDialog.open(InactiveIntegrationDialogComponent, {
        disableClose: false,
        panelClass: 'sidepanel-dialog-container',
      });
      return;
    }

    const addMcpServerDialogRef = this._matDialog.open(AddMcpServerDialogComponent, {
      disableClose: false,
      panelClass: 'sidepanel-dialog-container',
    });
    addMcpServerDialogRef.afterClosed().subscribe((mcpServersToAdd: McpServerModel[]) => {
      if (mcpServersToAdd && mcpServersToAdd.length > 0) {
        this._addMcpServers(mcpServersToAdd);
      }
    });
  }

  public removeMcpServerClicked(mcpServer: IMcpServerViewModel): void {
    this._mcpServersViewModel = this._mcpServersViewModel.filter((server) => server.url !== mcpServer.url);
    this.mcpServersCurrentPage$.next(this._mcpServersViewModel);
    this._resetSearchBox();
    this.changesWereDone = true;
    this.notifyChanges.emit(this.changesWereDone);
  }

  private _addMcpServers(mcpServersToAdd: McpServerModel[]): void {
    const newMcpServers = mcpServersToAdd.map((mcpServer) => this._transformToMcpServerViewModel(mcpServer));
    this._mcpServersViewModel = [...this._mcpServersViewModel, ...newMcpServers];
    this.mcpServersCurrentPage$.next(this._mcpServersViewModel);
    this._resetSearchBox();
    this.changesWereDone = true;
    this.notifyChanges.emit(this.changesWereDone);
  }

  public saveChanges(): void {
    this._updatePolicyConfig();
  }

  public resetForm(): void {
    this._mcpServersViewModel =
      this.policyModel.config.mcpServers.map((mcpServer) => this._transformToMcpServerViewModel(mcpServer)) || [];
    this.mcpServersCurrentPage$.next(this._mcpServersViewModel);
    this.mcpServersUrls = this._mcpServersViewModel.map((mcpServer) => mcpServer.url);
    this._resetSearchBox();
    this.changesWereDone = false;
    this.notifyChanges.emit(this.changesWereDone);
  }

  private _transformToMcpServerViewModel(mcpServer: McpServerModel): IMcpServerViewModel {
    return {
      url: mcpServer.url,
      clickable: false,
    };
  }

  private _resetSearchBox() {
    this.mcpServersUrls = this._mcpServersViewModel.map((mcpServer) => mcpServer.url);
    this.resetSearchBoxBehavioralSubject$.next(this.mcpServersUrls);
  }

  private _updatePolicyConfig() {
    // create the mcp servers model
    const mcpServers: McpServerModel[] = this._mcpServersViewModel.map(
      (mcpServer) =>
        ({
          url: mcpServer.url,
        }) as McpServerModel
    );
    this.isLoading$.next(true);

    this._policyConfigUpdateSubscription$ = this._policiesHttpService
      .updatePolicy(this.integrationModel.id, this.policyModel.id, this._generateUpdatePolicyModel(mcpServers))
      .subscribe({
        next: () => {
          this._notificationService.success('Policy updated successfully');
          this._policyEntitySubscription$ = this._policiesHttpService
            .getPolicy(this.integrationModel.id, this.policyModel.id)
            .subscribe({
              next: (response) => {
                this.policyModel = response;
                this.policyUpdated.emit(this.policyModel);
                this._mcpServersViewModel =
                  this.policyModel.config.mcpServers.map((mcpServer) =>
                    this._transformToMcpServerViewModel(mcpServer)
                  ) || [];
                this.mcpServersCurrentPage$.next(this._mcpServersViewModel);
                this._resetSearchBox();
              },
              error: (error) => this._httpErrorHandlerService.handle(error),
            });
          this.changesWereDone = false;
          this.notifyChanges.emit(this.changesWereDone);
          this.isLoading$.next(false);
        },
        error: (error) => {
          this._httpErrorHandlerService.handle(error);
          this.resetForm();
          this.isLoading$.next(false);
        },
      });
  }

  private _generateUpdatePolicyModel(mcpServers: McpServerModel[]): PolicyToUpdateModel {
    const currentConfig = _.cloneDeep(this.policyModel.config);
    currentConfig.mcpServers = mcpServers;
    return {
      name: this.policyModel.name,
      description: this.policyModel.description,
      type: this.policyModel.isDefault ? undefined : this.policyModel.type,
      config: currentConfig,
    } as PolicyToUpdateModel;
  }
}
